import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Send, CheckCircle, ChevronDown, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  heroTitle: { de: 'Karriere', en: 'Career' },
  heroSub: { de: 'Werde Teil unserer wachsenden Unternehmensgruppe. Entdecke aktuelle Stellenangebote unserer Tochtergesellschaften.', en: 'Become part of our growing corporate group. Discover current job openings from our subsidiaries.' },
  openPositions: { de: 'Offene Stellen', en: 'Open Positions' },
  filterAll: { de: 'Alle Unternehmen', en: 'All Companies' },
  clickToEnlarge: { de: 'Klicken zum Vergrößern', en: 'Click to enlarge' },
  applyNow: { de: 'Jetzt bewerben', en: 'Apply Now' },
  applyTitle: { de: 'Bewerbungsformular', en: 'Application Form' },
  applySub: { de: 'Interessiert an einer unserer offenen Stellen? Senden Sie uns Ihre Bewerbung.', en: 'Interested in one of our open positions? Send us your application.' },
  name: { de: 'Vollständiger Name', en: 'Full Name' },
  email: { de: 'E-Mail-Adresse', en: 'Email Address' },
  phone: { de: 'Telefonnummer (optional)', en: 'Phone Number (optional)' },
  position: { de: 'Gewünschte Stelle', en: 'Desired Position' },
  selectPosition: { de: 'Stelle auswählen...', en: 'Select position...' },
  company: { de: 'Unternehmen', en: 'Company' },
  selectCompany: { de: 'Unternehmen auswählen...', en: 'Select company...' },
  message: { de: 'Ihre Nachricht / Motivation', en: 'Your Message / Motivation' },
  messagePh: { de: 'Erzählen Sie uns kurz, warum Sie zu uns passen...', en: 'Tell us briefly why you are a good fit...' },
  privacy: { de: 'Ich habe die Datenschutzerklärung gelesen und stimme der Verarbeitung meiner personenbezogenen Daten zum Zweck der Bewerbungsbearbeitung zu. Meine Daten werden ausschließlich für diesen Zweck verwendet und nach Abschluss des Bewerbungsverfahrens (spätestens nach 6 Monaten) gelöscht.', en: 'I have read the privacy policy and consent to the processing of my personal data for the purpose of processing my application. My data will be used exclusively for this purpose and deleted after the application process is completed (at the latest after 6 months).' },
  privacyLink: { de: 'Datenschutzerklärung', en: 'Privacy Policy' },
  submit: { de: 'Bewerbung absenden', en: 'Submit Application' },
  sending: { de: 'Wird gesendet...', en: 'Sending...' },
  successTitle: { de: 'Bewerbung eingegangen!', en: 'Application Received!' },
  successMsg: { de: 'Vielen Dank für Ihre Bewerbung. Wir werden uns schnellstmöglich bei Ihnen melden.', en: 'Thank you for your application. We will get back to you as soon as possible.' },
  newApplication: { de: 'Weitere Bewerbung', en: 'New Application' },
  noPositions: { de: 'Aktuell keine offenen Stellen in diesem Unternehmen.', en: 'Currently no open positions in this company.' },
};

const JOB_FLYERS = [
  {
    id: 'bock-auf-strasse',
    image: '/images/karriere/bock-auf-strasse.png',
    company: 'AtoB Logistic Solutions GmbH',
    companyId: 'atob-logistic',
    position: 'LKW-Fahrer (m/w/d)',
    headline: 'Bock auf Straße?',
  },
  {
    id: 'fahrer-gesucht',
    image: '/images/karriere/fahrer-gesucht.png',
    company: 'AtoB Logistic Solutions GmbH',
    companyId: 'atob-logistic',
    position: 'LKW-Fahrer (m/w/d)',
    headline: 'Kein Job wie jeder andere',
  },
  {
    id: 'steig-ein',
    image: '/images/karriere/steig-ein.png',
    company: 'AtoB Logistic Solutions GmbH',
    companyId: 'atob-logistic',
    position: 'LKW-Fahrer National (m/w/d)',
    headline: 'Steig ein',
  },
  {
    id: 'neuer-job',
    image: '/images/karriere/neuer-job.png',
    company: 'AtoB Logistic Solutions GmbH',
    companyId: 'atob-logistic',
    position: 'LKW-Fahrer (m/w/d)',
    headline: 'Dein neuer Job wartet',
  },
];

const COMPANIES = [
  { id: 'atob-logistic', name: 'AtoB Logistic Solutions GmbH' },
  { id: 'dsb-business', name: 'DSB Business Solutions GmbH' },
  { id: 'atob-objektservice', name: 'AtoB Objektservice GmbH' },
];

export default function Karriere() {
  const { language } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const formRef = useRef(null);

  const filtered = filter === 'all' ? JOB_FLYERS : JOB_FLYERS.filter(j => j.companyId === filter);

  const scrollToForm = (position, company) => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
    window.__prefillApplication = { position, company };
    window.dispatchEvent(new Event('prefill-application'));
  };

  return (
    <main data-testid="karriere-page" className="pt-20">
      {/* Hero */}
      <section className="bg-[#0F1A2E] py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="karriere-grid" width="60" height="60" patternUnits="userSpaceOnUse"><path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5"/></pattern></defs><rect width="100%" height="100%" fill="url(#karriere-grid)" /></svg>
        </div>
        <div className="absolute -right-20 -top-20 w-96 h-96 bg-[#F5A623] opacity-5 transform rotate-45"></div>
        <div className="container-main relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="accent-line mb-6"></div>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-6">{T.heroTitle[language]}</h1>
            <p className="text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">{T.heroSub[language]}</p>
          </motion.div>
        </div>
      </section>

      {/* Job Flyers */}
      <section className="py-20 md:py-28 bg-[#0A1220]">
        <div className="container-main">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
            <div>
              <div className="accent-line mb-4"></div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white">{T.openPositions[language]}</h2>
            </div>
            <div className="flex flex-wrap gap-3" data-testid="company-filter">
              <button
                onClick={() => setFilter('all')}
                className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all ${filter === 'all' ? 'bg-[#F5A623] text-[#0F1A2E]' : 'bg-[#152238] text-white/70 border border-[#1e3a5f] hover:border-[#F5A623]/50'}`}
              >
                {T.filterAll[language]}
              </button>
              {COMPANIES.map(c => (
                <button
                  key={c.id}
                  onClick={() => setFilter(c.id)}
                  className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all ${filter === c.id ? 'bg-[#F5A623] text-[#0F1A2E]' : 'bg-[#152238] text-white/70 border border-[#1e3a5f] hover:border-[#F5A623]/50'}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="text-white/50 text-center py-16">{T.noPositions[language]}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filtered.map((job, i) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`job-flyer-${job.id}`}
                  className="group"
                >
                  <div className="bg-[#152238] rounded-2xl overflow-hidden border border-[#1e3a5f] hover:border-[#F5A623]/40 transition-all duration-300 hover:shadow-xl hover:shadow-[#F5A623]/5">
                    <div
                      className="relative cursor-pointer overflow-hidden"
                      onClick={() => setLightbox(job.image)}
                    >
                      <img
                        src={job.image}
                        alt={job.headline}
                        className="w-full aspect-[4/5] object-cover object-top group-hover:scale-[1.02] transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <span className="bg-white/90 text-[#0F1A2E] px-4 py-2 rounded-full text-xs font-semibold">{T.clickToEnlarge[language]}</span>
                      </div>
                    </div>
                    <div className="p-5">
                      <span className="text-[#F5A623] text-xs font-semibold uppercase tracking-wider">{job.company}</span>
                      <h3 className="text-white font-semibold mt-1 mb-3">{job.position}</h3>
                      <button
                        onClick={() => scrollToForm(job.position, job.company)}
                        data-testid={`apply-btn-${job.id}`}
                        className="w-full bg-[#F5A623] text-[#0F1A2E] py-3 text-sm font-bold uppercase tracking-wider rounded-lg hover:bg-white transition-colors flex items-center justify-center gap-2"
                      >
                        {T.applyNow[language]} <ChevronDown size={16} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Application Form */}
      <section ref={formRef} className="py-20 md:py-28 bg-[#0F1A2E]" data-testid="application-form-section">
        <div className="container-main">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <div className="flex justify-center mb-6"><div className="accent-line"></div></div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">{T.applyTitle[language]}</h2>
              <p className="text-white/60">{T.applySub[language]}</p>
            </div>
            <ApplicationForm language={language} />
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 text-white/70 hover:text-white"><X size={32} /></button>
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg" />
        </div>
      )}
    </main>
  );
}

function ApplicationForm({ language }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', position: '', company: '', message: '', privacy_accepted: false });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const positions = [...new Set(JOB_FLYERS.map(j => j.position))];

  // Listen for prefill events
  useState(() => {
    const handler = () => {
      const data = window.__prefillApplication;
      if (data) {
        setForm(f => ({ ...f, position: data.position, company: data.company }));
        delete window.__prefillApplication;
      }
    };
    window.addEventListener('prefill-application', handler);
    return () => window.removeEventListener('prefill-application', handler);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/karriere/bewerbung`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Fehler beim Senden');
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  if (success) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#152238] rounded-2xl border border-[#1e3a5f] p-12 text-center">
        <CheckCircle size={56} className="text-[#F5A623] mx-auto mb-6" />
        <h3 className="text-2xl font-bold text-white mb-3">{T.successTitle[language]}</h3>
        <p className="text-white/60 mb-8">{T.successMsg[language]}</p>
        <button onClick={() => { setSuccess(false); setForm({ name: '', email: '', phone: '', position: '', company: '', message: '', privacy_accepted: false }); }} className="text-[#F5A623] font-semibold hover:text-white transition-colors">
          {T.newApplication[language]}
        </button>
      </motion.div>
    );
  }

  const inputCls = "w-full bg-[#0A1220] border border-[#1e3a5f] rounded-xl px-5 py-4 text-white placeholder-white/30 focus:border-[#F5A623] focus:outline-none transition-colors";

  return (
    <form onSubmit={handleSubmit} className="bg-[#152238] rounded-2xl border border-[#1e3a5f] p-8 md:p-10 space-y-6" data-testid="application-form">
      <div>
        <label className="block text-white/70 text-sm mb-2 font-medium">{T.name[language]} *</label>
        <input type="text" required value={form.name} onChange={e => update('name', e.target.value)} className={inputCls} data-testid="input-name" />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-white/70 text-sm mb-2 font-medium">{T.email[language]} *</label>
          <input type="email" required value={form.email} onChange={e => update('email', e.target.value)} className={inputCls} data-testid="input-email" />
        </div>
        <div>
          <label className="block text-white/70 text-sm mb-2 font-medium">{T.phone[language]}</label>
          <input type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} className={inputCls} data-testid="input-phone" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className="block text-white/70 text-sm mb-2 font-medium">{T.position[language]} *</label>
          <select required value={form.position} onChange={e => update('position', e.target.value)} className={inputCls} data-testid="select-position">
            <option value="">{T.selectPosition[language]}</option>
            {positions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-white/70 text-sm mb-2 font-medium">{T.company[language]} *</label>
          <select required value={form.company} onChange={e => update('company', e.target.value)} className={inputCls} data-testid="select-company">
            <option value="">{T.selectCompany[language]}</option>
            {COMPANIES.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-white/70 text-sm mb-2 font-medium">{T.message[language]}</label>
        <textarea rows={4} value={form.message} onChange={e => update('message', e.target.value)} className={`${inputCls} resize-none`} placeholder={T.messagePh[language]} data-testid="input-message"></textarea>
      </div>

      <div className="flex items-start gap-3 p-4 bg-[#0A1220] rounded-xl border border-[#1e3a5f]">
        <input type="checkbox" required checked={form.privacy_accepted} onChange={e => update('privacy_accepted', e.target.checked)} className="mt-1 w-5 h-5 accent-[#F5A623] flex-shrink-0" data-testid="checkbox-privacy" />
        <p className="text-white/50 text-sm leading-relaxed">
          {T.privacy[language]}{' '}
          <a href="/datenschutz" target="_blank" className="text-[#F5A623] hover:text-white transition-colors underline">{T.privacyLink[language]}</a>
        </p>
      </div>

      {error && <p className="text-red-400 text-sm" data-testid="form-error">{error}</p>}

      <button
        type="submit"
        disabled={sending}
        data-testid="submit-application"
        className="w-full bg-[#F5A623] text-[#0F1A2E] py-4 text-sm font-bold uppercase tracking-wider rounded-xl hover:bg-white transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
      >
        {sending ? T.sending[language] : T.submit[language]}
        <Send size={18} />
      </button>
    </form>
  );
}
